import { useScrollReveal } from '@/hooks/useScrollReveal';

const dataPoints = [
  {
    icon: 'ri-radar-line',
    value: '85,000+',
    label: 'Tonnes Capacity Mapped',
    desc: 'Real-time visibility into available freezing and cold-storage capacity across 28 cities and growing.',
  },
  {
    icon: 'ri-line-chart-line',
    value: '94%',
    label: 'Utilisation Prediction Accuracy',
    desc: 'Our engine forecasts warehouse utilisation windows 30 days ahead, helping owners maximise revenue and buyers secure space before harvest peaks.',
  },
  {
    icon: 'ri-calendar-check-line',
    value: 'Seasonal',
    label: 'Harvest Calendar Intelligence',
    desc: 'We map India\'s regional harvest calendars — seafood in Kerala, poultry in Gujarat, vegetables in Punjab — to predict demand surges before they happen.',
  },
  {
    icon: 'ri-thermometer-line',
    value: 'Real-Time',
    label: 'IoT Temperature Monitoring',
    desc: 'Every connected facility streams temperature data to our platform. Anomalies trigger automatic alerts — no batch spoils without someone knowing.',
  },
];

export default function DataAdvantageSection() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-16 md:py-20 bg-secondary-50/30">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16">
          {/* Left: narrative */}
          <div className="w-full lg:w-2/5">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-100/70 border border-secondary-200/50 mb-4">
              <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wider">Our Moat</span>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary-700 mb-5">
              The Data Nobody Else Has
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed mb-5">
              India&apos;s cold chain is fragmented by design — hundreds of independent warehouses, thousands of reefer vehicles, millions of transactions. No single player sees the whole picture.
            </p>
            <p className="text-foreground-600 text-sm leading-relaxed mb-5">
              <strong className="text-secondary-700 font-semibold">Freshhlock does.</strong> Because we sit at the centre — connected to storage, transport, wholesale and retail — we build a real-time map of India&apos;s frozen food capacity that no competitor can replicate. Every warehouse that lists with us, every vehicle that books a route, every trade that clears through our marketplace makes the map more accurate.
            </p>
            <p className="text-foreground-500 text-sm leading-relaxed">
              This isn&apos;t just data. It&apos;s a <strong className="text-secondary-700 font-semibold">defensible competitive moat</strong> — the kind of network-effect advantage that gets stronger with every new participant.
            </p>
          </div>

          {/* Right: data point cards */}
          <div className="w-full lg:w-3/5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {dataPoints.map((dp) => (
                <div key={dp.label} className="group bg-card-50 rounded-xl border border-secondary-200/30 p-6 shadow-sm hover:shadow-md hover:border-secondary-300/50 transition-all cursor-default card-animate">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center mb-4 group-hover:bg-secondary-200 transition-colors animate-icon-float icon-hover-pop">
                    <i className={`${dp.icon} text-lg text-secondary-600`}></i>
                  </div>
                  <div className="font-heading text-2xl font-bold text-secondary-700 mb-1 tabular-nums">{dp.value}</div>
                  <div className="text-sm font-semibold text-foreground-800 mb-2">{dp.label}</div>
                  <p className="text-xs text-foreground-500 leading-relaxed">{dp.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}